import { Controller } from '@hotwired/stimulus'

// 費目解説モーダル本体。SP の component_controller がこのクラスを継承して使う
// （PCはホバー表示なのでモーダルを持たない）。
// window 経由の fee-description-modal:open / Escape / Tab の購読は
// slim 側の @window 記法で宣言済みのため、ここで addEventListener は行わない。
export default class extends Controller {
  static targets = ['modal', 'dialog', 'content', 'item', 'close']

  get className () {
    return this.element.dataset.classname
  }

  get isOpen () {
    return this.modalTarget.classList.contains(`${this.className}::is-open`)
  }

  // モーダルが開いたまま要素が外れると overflow:hidden がリークして
  // 次の画面でスクロール不能になるため、disconnect でbody/htmlのoverflowを復元する
  // （map_modal と同じ扱い）。開いていないときに触ると、同居する他のモーダルが掛けた
  // ロックを解除してしまうため、自分が掛けたぶんだけを外す（下記 scrollLocked）。
  //
  // is-open と openerElement / savedScrollY も落として、古い参照を残さない。
  //
  // NOTE: 現状のこのリポジトリに「開いたまま disconnect される」具体的な経路は無い
  //       （turbo / turbolinks は未導入でbfcache復帰ではStimulusが再接続しない。
  //         async_frame_controller は innerHTML ごと差し替えるため再接続されるのは別インスタンスで、
  //         そもそも施設詳細の料金セクションは async-frame 配下にない）。
  //       将来そうした経路が増えたときに黙って壊れないための防御として残している。
  disconnect () {
    // スクロールロックの解除は「自分が掛けたか」だけで判断する。
    // modalTarget や is-open の有無で判断すると、target だけが先に外れる経路で
    // 解除ごとスキップされ、防ぎたい overflow:hidden のリークをそのまま通してしまう
    if (this.scrollLocked) this.unlockScroll()
    if (this.hasModalTarget) this.modalTarget.classList.remove(`${this.className}::is-open`)

    this.openerElement = null
    this.savedScrollY = null
  }

  open (event) {
    // 開いている最中に再度イベントが飛んでも openerElement / savedScrollY を上書きしない
    // （上書きするとフォーカスの戻り先とスクロール位置の復帰先を失う）
    if (this.isOpen) {
      this.scrollToItem(event?.detail?.slug)
      return
    }

    this.openerElement = document.activeElement
    this.savedScrollY = window.scrollY
    this.modalTarget.classList.add(`${this.className}::is-open`)
    this.lockScroll()

    // 開き直したときに前回のスクロール位置が残らないよう先頭へ戻す
    if (this.hasContentTarget) this.contentTarget.scrollTop = 0
    this.scrollToItem(event?.detail?.slug)

    // aria-modal="true" を宣言している以上、フォーカスをダイアログ内へ移す
    if (this.hasCloseTarget) this.closeTarget.focus()
  }

  // Escapeは @window で拾うため、閉じているときは何もしない
  close () {
    if (!this.isOpen) return

    this.modalTarget.classList.remove(`${this.className}::is-open`)
    this.unlockScroll()
    window.scrollTo(0, this.savedScrollY || 0)

    // 呼び出し元の?ボタンへフォーカスを戻す（キーボード利用者が読み位置を失わないように）
    if (this.openerElement && document.contains(this.openerElement)) this.openerElement.focus()
  }

  // aria-modal="true" を宣言している以上、Tabキーでダイアログの外へ抜けさせない。
  // 現状の中身は閉じるボタン1つだけだが、項目が増えても成り立つよう
  // フォーカス可能な要素の先頭と末尾で循環させる。
  trapFocus (event) {
    if (!this.isOpen || !this.hasDialogTarget) return

    const focusables = this.focusableElements
    if (focusables.length === 0) return

    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    const active = document.activeElement
    const outside = !this.dialogTarget.contains(active)

    if (event.shiftKey && (active === first || outside)) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault()
      first.focus()
    }
  }

  get focusableElements () {
    const selector = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

    // 可視判定に offsetParent を使わない。position: fixed の要素は offsetParent が null になるため、
    // ダイアログの配置を fixed に変えた瞬間に候補が0件になり、フォーカストラップが無言で無効化される
    return Array.from(this.dialogTarget.querySelectorAll(selector))
      .filter(element => !element.disabled && element.getClientRects().length > 0)
  }

  // 押された?ボタンの費目までスクロールする。
  // モーダルは全費目をまとめて出すため、これが無いとラベルと中身が噛み合わない
  scrollToItem (slug) {
    if (!slug || !this.hasItemTarget) return

    const item = this.itemTargets.find(element => element.dataset.feeSlug === slug)
    if (!item || !this.hasContentTarget) return

    // offsetTop の差分にすると .content の position（現状は static）に依存し、
    // relative が付いた瞬間に基準が変わって二重に引いてしまうため矩形の差分で求める
    const itemTop = item.getBoundingClientRect().top
    const contentTop = this.contentTarget.getBoundingClientRect().top
    this.contentTarget.scrollTop += itemTop - contentTop
  }

  // 自分が掛けたロックだけを外せるようフラグを持つ（disconnect のコメント参照）。
  // 同居する他のモーダルが掛けたロックを、開いていないこちらが解除してしまうのも防ぐ
  lockScroll () {
    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    this.scrollLocked = true
  }

  unlockScroll () {
    document.body.style.removeProperty('overflow')
    document.documentElement.style.removeProperty('overflow')
    this.scrollLocked = false
  }
}
